import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Text,
  useColorModeValue,
  Card,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Icon,
  Spinner,
  Badge,
} from "@chakra-ui/react";
import {
  MdAttachMoney,
  MdTrendingUp,
  MdBusiness,
  MdTableRestaurant,
} from "react-icons/md";
import { pricingService } from "../../../../services/pricingService";
import { paymentService } from "../../../../services/paymentService";

export default function PriceAnalysis() {
  // Tüm hook'lar en üstte - early return'lerden önce
  const [pricingData, setPricingData] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const brandColor = useColorModeValue("#364fab", "#364fab");
  const cardBg = useColorModeValue("white", "navy.800");
  const borderColor = useColorModeValue("gray.100", "whiteAlpha.100");
  const hoverBg = useColorModeValue("gray.50", "whiteAlpha.50");
  const hoverBgActive = useColorModeValue("gray.100", "whiteAlpha.100");
  const brandLight = useColorModeValue("#4A6BC7", "#5B7DD8");

  useEffect(() => {
    fetchPricingData();
    fetchPaymentStatus();
  }, []);

  const fetchPricingData = async () => {
    setIsLoading(true);
    try {
      const response = await pricingService.getPricingInfo();
      if (response.success) {
        setPricingData(response.data);
      }
    } catch (error) {
      console.error("Fiyatlandırma verisi getirilirken hata:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPaymentStatus = async () => {
    try {
      const response = await paymentService.getCurrentMonthPaymentStatus();
      if (response.success) {
        setPaymentStatus(response.data);
      }
    } catch (error) {
      console.error("Ödeme durumu getirilirken hata:", error);
    }
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" h="200px">
        <Spinner size="xl" color={brandColor} />
      </Flex>
    );
  }

  if (!pricingData) {
    return (
      <Card p={6}>
        <Text color={textColor} fontSize="lg">
          Fiyatlandırma bilgisi bulunamadı
        </Text>
      </Card>
    );
  }

  const { business, restaurant, tableCount, currentMonthRevenue, currentMonthOrderCount, pricing } = pricingData;
  const currencySymbol = "₺";

  return (
    <Box>
      {/* Özel İndirim Bilgisi */}
      {pricing.specialDiscount > 0 && (
        <Card 
          bg={cardBg} 
          p={6}
          borderRadius="xl"
          border="1px solid"
          borderColor="green.200"
          boxShadow="sm"
          mb="20px"
        >
          <Flex align="center" mb={3}>
            <Badge
              colorScheme="green"
              fontSize="sm"
              px={3}
              py={1}
              borderRadius="full"
              mr={3}
            >
              Özel İndirim Aktif
            </Badge>
            <Text color="green.600" fontSize="lg" fontWeight="700">
              %{pricing.specialDiscount} İndirim Uygulanıyor
            </Text>
          </Flex>
          {pricing.discountAmount > 0 && (
            <Text color={textColor} fontSize="md" mb={2}>
              <strong>İndirim Tutarı:</strong> {currencySymbol}{pricing.discountAmount.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </Text>
          )}
          {pricing.discountReason && (
            <Box
              mt={3}
              p={3}
              bg={hoverBg}
              borderRadius="md"
              borderLeft="3px solid"
              borderColor="green.500"
            >
              <Text color="gray.600" fontSize="sm" fontWeight="500" mb={1}>
                İndirim Açıklaması:
              </Text>
              <Text color={textColor} fontSize="sm">
                {pricing.discountReason}
              </Text>
            </Box>
          )}
        </Card>
      )}


      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={6}>
        <Card 
          bg={cardBg} 
          p={4}
          borderRadius="lg"
          border="1px solid"
          borderColor={borderColor}
          boxShadow="sm"
          _hover={{ 
            boxShadow: "md",
            transform: "translateY(-2px)",
            transition: "all 0.2s"
          }}
        >
          <Stat>
            <Flex align="center" mb={2}>
              <Box
                p={1.5}
                borderRadius="md"
                bg={`${brandColor}15`}
                mr={2}
              >
                <Icon as={MdTableRestaurant} color={brandColor} boxSize={4} />
              </Box>
              <StatLabel color="gray.500" fontSize="xs" fontWeight="500">
                Masa Sayısı
              </StatLabel>
            </Flex>
            <Flex align="center" justify="space-between">
              <StatNumber fontSize="xl" color={textColor} fontWeight="700">
                {tableCount}
              </StatNumber>
              {pricing.plan && (
                <Badge 
                  colorScheme={pricing.plan === "PREMIUM" ? "yellow" : "blue"}
                  fontSize="2xs"
                  px={1.5}
                  py={0.5}
                  borderRadius="sm"
                  ml={2}
                >
                  {pricing.plan}
                </Badge>
              )}
            </Flex>
          </Stat>
        </Card>

        <Card 
          bg={cardBg} 
          p={4}
          borderRadius="lg"
          border="1px solid"
          borderColor={borderColor}
          boxShadow="sm"
          _hover={{ 
            boxShadow: "md",
            transform: "translateY(-2px)",
            transition: "all 0.2s"
          }}
        >
          <Stat>
            <Flex align="center" mb={2}>
              <Box
                p={1.5}
                borderRadius="md"
                bg="green.50"
                _dark={{ bg: "green.900" }}
                mr={2}
              >
                <Icon as={MdTrendingUp} color="green.500" boxSize={4} />
              </Box>
              <StatLabel color="gray.500" fontSize="xs" fontWeight="500">
                Bu Ayın Cirosu
              </StatLabel>
            </Flex>
            <StatNumber fontSize="xl" color={textColor} fontWeight="700">
              {currencySymbol}{currentMonthRevenue.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </StatNumber>
          </Stat>
        </Card>

        <Card 
          bg={cardBg} 
          p={4}
          borderRadius="lg"
          border="1px solid"
          borderColor={borderColor}
          boxShadow="sm"
          _hover={{ 
            boxShadow: "md",
            transform: "translateY(-2px)",
            transition: "all 0.2s"
          }}
        >
          <Stat>
            <Flex align="center" mb={2}>
              <Box
                p={1.5}
                borderRadius="md"
                bg={`${brandColor}15`}
                mr={2}
              >
                <Icon as={MdBusiness} color={brandColor} boxSize={4} />
              </Box>
              <StatLabel color="gray.500" fontSize="xs" fontWeight="500">
                Sipariş Sayısı
              </StatLabel>
            </Flex>
            <StatNumber fontSize="xl" color={textColor} fontWeight="700">
              {currentMonthOrderCount || pricing.monthlyOrderCount || 0}
            </StatNumber>
          </Stat>
        </Card>

        <Card 
          bg={`linear-gradient(135deg, ${brandColor} 0%, ${brandLight} 100%)`}
          p={4}
          borderRadius="lg"
          boxShadow="lg"
          _hover={{ 
            boxShadow: "xl",
            transform: "translateY(-2px)",
            transition: "all 0.2s"
          }}
        >
          <Stat>
            <Flex align="center" mb={2}>
              <Box
                p={1.5}
                borderRadius="md"
                bg="whiteAlpha.20"
                mr={2}
              >
                <Icon as={MdAttachMoney} color="white" boxSize={4} />
              </Box>
              <StatLabel color="whiteAlpha.900" fontSize="xs" fontWeight="500">
                Bu Ay Ödeyeceğiniz Tutar
              </StatLabel>
            </Flex>
            <Box>
              {/* Bu Ay Ödeyeceğiniz Tutar: Sabit Ücret + Komisyon (KDV Dahil) + Ay Sonu Yansıyacak Fark + Nakit Ödemelerden Gelen Komisyonlar (kart kayıtlı değilse) - İndirim (varsa) */}
              {(() => {
                /**
                 * Backend: commissionAmount BRÜT (KDV dahil). estimatedTotalFee = max(0, finalTotalFee - collected);
                 * finalTotalFee zaten indirim sonrası. commissionDifference ayrıca toplanmaz (çifte komisyon hatası).
                 */
                const cm = paymentStatus?.currentMonth;
                const prepaid = cm?.prepaidCommission || 0;
                const estimatedFromApi =
                  cm != null && typeof cm.estimatedTotalFee === 'number'
                    ? cm.estimatedTotalFee
                    : null;

                const finalTotal =
                  estimatedFromApi != null
                    ? estimatedFromApi
                    : Math.max(
                        0,
                        (pricing.finalTotalFee != null
                          ? pricing.finalTotalFee
                          : (pricing.baseMonthlyFee || pricing.monthlyFee || 0) +
                            (pricing.commissionAmount || 0)) - prepaid
                      );

                const preDiscountEstimated =
                  pricing.specialDiscount > 0
                    ? Math.max(
                        0,
                        (pricing.currentMonthTotal ??
                          (pricing.baseMonthlyFee || pricing.monthlyFee || 0) +
                            (pricing.commissionAmount || 0)) - prepaid
                      )
                    : 0;

                return (
                  <>
                    {pricing.specialDiscount > 0 &&
                      preDiscountEstimated > 0 &&
                      preDiscountEstimated > finalTotal && (
                      <Text
                        fontSize="sm"
                        color="whiteAlpha.700"
                        textDecoration="line-through"
                        mb={1}
                      >
                        {currencySymbol}{preDiscountEstimated.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Text>
                    )}
                    <StatNumber fontSize="xl" color="white" fontWeight="700">
                      {currencySymbol}{finalTotal.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </StatNumber>
                    {/* İndirim bilgisi */}
                    {pricing.specialDiscount > 0 && (
                      <Badge
                        mt={1}
                        colorScheme="green"
                        fontSize="2xs"
                        px={2}
                        py={0.5}
                        borderRadius="sm"
                      >
                        %{pricing.specialDiscount} İndirim
                      </Badge>
                    )}
                  </>
                );
              })()}
            </Box>
          </Stat>
        </Card>
      </SimpleGrid>

      <Card 
        bg={cardBg} 
        p={8}
        borderRadius="xl"
        border="1px solid"
        borderColor={borderColor}
        boxShadow="sm"
        mb="20px"
      >
        <Text 
          color={textColor} 
          fontSize="xl" 
          fontWeight="700" 
          mb={8}
          position="relative"
          _after={{
            content: '""',
            position: "absolute",
            bottom: "-12px",
            left: 0,
            width: "60px",
            height: "3px",
            bg: brandColor,
            borderRadius: "full"
          }}
        >
          Ücret Detayları
        </Text>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8} mt={8}>
          <Box
            p={5}
            borderRadius="lg"
            bg={hoverBg}
            borderLeft="4px solid"
            borderColor={brandColor}
            _hover={{
              bg: hoverBgActive,
              transform: "translateX(4px)",
              transition: "all 0.2s"
            }}
          >
            <Text color="gray.500" fontSize="sm" mb={3} fontWeight="500">
              Sabit Plan Ücreti
            </Text>
            <Text color={brandColor} fontSize="3xl" fontWeight="800">
              {currencySymbol}{(pricing.baseMonthlyFee || pricing.monthlyFee).toLocaleString(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}
            </Text>
          </Box>
          <Box
            p={5}
            borderRadius="lg"
            bg={hoverBg}
            borderLeft="4px solid"
            borderColor={brandColor}
            _hover={{
              bg: hoverBgActive,
              transform: "translateX(4px)",
              transition: "all 0.2s"
            }}
          >
            <Text color="gray.500" fontSize="sm" mb={3} fontWeight="500">
              Ortalama Komisyon Oranı
            </Text>
            <Text color={brandColor} fontSize="3xl" fontWeight="800">
              %{pricing.averageCommissionRate?.toFixed(2) || pricing.commissionRate?.toFixed(1) || '0.0'}
            </Text>
            {/* Oran değişikliği uyarısı */}
            {pricing.commissionBreakdown && pricing.commissionBreakdown.length > 1 && (() => {
              const firstTier = pricing.commissionBreakdown[0];
              const lastTier = pricing.commissionBreakdown[pricing.commissionBreakdown.length - 1];
              const rateDecreased = lastTier.rate < firstTier.rate;
              
              if (rateDecreased) {
                return (
                  <Box mt={3} p={3} bg="green.50" borderRadius="md" borderLeft="4px solid" borderColor="green.500">
                    <Flex align="center" mb={1}>
                      <Text color="green.700" fontSize="sm" fontWeight="bold" mr={2}>
                        🎉 Komisyon Oranınız Düştü! Menomlu olduğunuz için teşekkür ederiz.
                      </Text>
                    </Flex>
                  </Box>
                );
              }
              return null;
            })()}
          </Box>
          <Box
            p={5}
            borderRadius="lg"
            bg={hoverBg}
            borderLeft="4px solid"
            borderColor={paymentStatus?.currentMonth?.commissionDifference > 0 && pricing.commissionRate > 3.0 ? "orange.500" : brandColor}
            _hover={{
              bg: hoverBgActive,
              transform: "translateX(4px)",
              transition: "all 0.2s"
            }}
          >
            <Text color="gray.500" fontSize="sm" mb={3} fontWeight="500">
              Ay Sonu Yansıyacak Fark
            </Text>
            {pricing.commissionRate > 3.0 && paymentStatus?.currentMonth?.commissionDifference > 0 ? (
              <>
                <Text 
                  color="orange.500" 
                  fontSize="3xl" 
                  fontWeight="800"
                >
                  {currencySymbol}{(paymentStatus?.currentMonth?.commissionDifference || 0).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Text>
                <Text color="gray.600" fontSize="xs" mt={1}>
                  Ek ödeme gerekli (Komisyon oranı %{pricing.commissionRate})
                </Text>
              </>
            ) : (
              <>
                <Text 
                  color={brandColor} 
                  fontSize="3xl" 
                  fontWeight="800"
                >
                  {currencySymbol}0.00
                </Text>
                <Text color="gray.600" fontSize="xs" mt={1}>
                  {pricing.commissionRate === 3.0 ? 'Komisyon oranı %3, fark yok' : 'Fark yok'}
                </Text>
              </>
            )}
          </Box>
        </SimpleGrid>
      </Card>

      {/* YENİ ÖDEME SİSTEMİ: Ödeme Durumu */}
      {paymentStatus && (
        <Card 
          bg={cardBg} 
          p={6}
          borderRadius="xl"
          border="1px solid"
          borderColor={borderColor}
          boxShadow="sm"
          mb="20px"
        >
          <Text 
            color={textColor} 
            fontSize="xl" 
            fontWeight="700" 
            mb={6}
            position="relative"
            _after={{
              content: '""',
              position: "absolute",
              bottom: "-12px",
              left: 0,
              width: "60px",
              height: "3px",
              bg: brandColor,
              borderRadius: "full"
            }}
          >
            Ödeme Durumu
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4} mt={8}>
            <Box
              p={5}
              borderRadius="lg"
              bg={hoverBg}
              borderLeft="4px solid"
              borderColor={paymentStatus.cardStatus.isCardSaved ? "green.500" : "red.500"}
              _hover={{
                bg: hoverBgActive,
                transform: "translateX(4px)",
                transition: "all 0.2s"
              }}
            >
              <Text color="gray.500" fontSize="sm" mb={3} fontWeight="500">
                Kart Durumu
              </Text>
              <Text 
                color={paymentStatus.cardStatus.isCardSaved ? "green.500" : "red.500"} 
                fontSize="lg" 
                fontWeight="700"
              >
                {paymentStatus.cardStatus.isCardSaved 
                  ? `•••• ${paymentStatus.cardStatus.cardLastFour}` 
                  : 'Kart Kayıtlı Değil'}
              </Text>
              {paymentStatus.cardStatus.isCardSaved && (
                <Text color="gray.600" fontSize="xs" mt={1}>
                  {paymentStatus.cardStatus.cardBrand}
                </Text>
              )}
            </Box>
            {paymentStatus.lastPayment && (
              <Box
                p={5}
                borderRadius="lg"
                bg={hoverBg}
                borderLeft="4px solid"
                borderColor="gray.500"
                _hover={{
                  bg: hoverBgActive,
                  transform: "translateX(4px)",
                  transition: "all 0.2s"
                }}
              >
                <Text color="gray.500" fontSize="sm" mb={3} fontWeight="500">
                  Geçen Ay Ödeme Durumu
                </Text>
                <Text color={textColor} fontSize="md" fontWeight="600" mb={1}>
                  {new Date(paymentStatus.lastPayment.year, paymentStatus.lastPayment.month).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long' })}
                </Text>
                <Text color="gray.600" fontSize="sm" mb={2}>
                  {currencySymbol}{paymentStatus.lastPayment.finalTotalFee.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Text>
                <Badge
                  colorScheme={
                    paymentStatus.lastPayment.status === 'paid' ? 'green' :
                    paymentStatus.lastPayment.status === 'pending' ? 'yellow' :
                    paymentStatus.lastPayment.status === 'failed' ? 'red' :
                    paymentStatus.lastPayment.status === 'overdue' ? 'orange' : 'gray'
                  }
                  fontSize="xs"
                  px={2}
                  py={0.5}
                  borderRadius="full"
                >
                  {paymentStatus.lastPayment.status === 'paid' ? 'Ödendi' :
                   paymentStatus.lastPayment.status === 'pending' ? 'Beklemede' :
                   paymentStatus.lastPayment.status === 'failed' ? 'Başarısız' :
                   paymentStatus.lastPayment.status === 'overdue' ? 'Gecikmiş' : paymentStatus.lastPayment.status}
                </Badge>
              </Box>
            )}
          </SimpleGrid>
        </Card>
      )}
    </Box>
  );
}

